import { Action } from '@components/icons'
import config from '@config/seo_meta.json'
import cn from 'clsx'
import { useTranslation } from 'next-i18next'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { FC, useState } from 'react'

interface Article {
  name: string
  title: string
  blogCategory: string
  thumbnail: {
    url: string
    alt: string
    copyright: string
    dimensions: {
      width: number
      height: number
    }
  }
}
interface Props {
  uid: string
  article: Article
  featured?: boolean
}

const ArticleCard: FC<Props> = ({ uid, article, featured = false }: Props) => {
  const { name, title, blogCategory, thumbnail } = article || {}
  const [isHover, setIsHover] = useState(false)
  const { t } = useTranslation('common')
  const handleHover = () => {
    setIsHover(true)
  }
  const handleLeave = () => {
    setIsHover(false)
  }
  if (!article || !uid) return <></>
  return (
    <>
      <div onMouseOver={handleHover} onMouseLeave={handleLeave}>
        <Link href={`/article/${uid}`} passHref legacyBehavior>
          <div
            className={cn('flex w-full cursor-pointer lg:flex-col lg:gap-0 ', {
              'flex-row gap-4': !featured,
              'flex-col gap-3': featured,
            })}
          >
            {!featured && thumbnail ? (
              <div>
                <div className="hidden lg:block">
                  <Image
                    src={thumbnail?.url || '/product-img-placeholder.svg'}
                    alt={thumbnail?.alt || config.openGraph.images[0].alt}
                    layout="responsive"
                    width={304}
                    height={405}
                    objectFit="cover"
                  />
                </div>
                <div className="block h-[139px] w-[104px] lg:hidden">
                  <Image
                    src={thumbnail?.url || '/product-img-placeholder.svg'}
                    alt={thumbnail?.alt || config.openGraph.images[0].alt}
                    layout="responsive"
                    width={104}
                    height={139}
                    objectFit="cover"
                  />
                </div>
              </div>
            ) : (
              <Image
                src={thumbnail?.url || '/product-img-placeholder.svg'}
                alt={thumbnail?.alt || config.openGraph.images[0].alt}
                layout="responsive"
                width={304}
                height={405}
                objectFit="cover"
              />
            )}

            <div
              className={cn('flex flex-col pt-0 lg:pt-2', {
                'gap-2': !featured,
                'gap-3': featured,
              })}
            >
              {blogCategory && (
                <div className="label-bold uppercase text-brand-dark-grey">
                  {featured && `${t(`featured`)} `}
                  {t(blogCategory.toLowerCase())}
                </div>
              )}
              {name && (
                <div
                  className={cn('text-brand-dark-grey', {
                    'heading-4 min-h-16': !featured,
                    'heading-mobile-4 min-h-13': featured,
                  })}
                >
                  {name}
                </div>
              )}
              {title && (
                <div
                  className={cn('body-1 text-brand-dark-grey', {
                    ' line-clamp-2': !featured,
                    'line-clamp-4': featured,
                  })}
                >
                  {title}
                </div>
              )}
              {uid && (
                <div>
                  <div
                    className={cn('flex items-center', {
                      'gap-3': !featured,
                      'gap-2': featured,
                    })}
                  >
                    <div
                      className={cn(' text-brand-dark-grey ', {
                        'body-1': !featured,
                        'body-2': featured,
                      })}
                    >
                      {t('read-more')}
                    </div>
                    {!featured ? (
                      <div>
                        {isHover ? <Action hover></Action> : <Action></Action>}
                      </div>
                    ) : (
                      <div>
                        {isHover ? (
                          <Action width="20" height="20" hover></Action>
                        ) : (
                          <Action width="20" height="20"></Action>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Link>
      </div>
    </>
  )
}

export default ArticleCard
